/**
 * "View as": an administrator tests Pulse with another user's or role's access (not available in production).
 *
 * The choice lives in sessionStorage, so it only affects this browser tab and ends when the tab closes. Every API
 * call carries it as `X-Pulse-View-As`; the server checks the caller really is an administrator.
 */

export const VIEW_AS_HEADER = "X-Pulse-View-As";
const KEY = "pulse.viewAs";

export interface ViewAsTarget {
  kind: "user" | "role";
  id: string;
}

export function getViewAs(): ViewAsTarget | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ViewAsTarget>;
    return (parsed.kind === "user" || parsed.kind === "role") && typeof parsed.id === "string"
      ? { kind: parsed.kind, id: parsed.id }
      : null;
  } catch {
    return null;
  }
}

export function viewAsHeader(): string | null {
  const target = getViewAs();
  return target ? `${target.kind}:${target.id}` : null;
}

/** Reload from the home page so every cached query, menu and permission is rebuilt for the new access. */
function reloadHome(): void {
  window.location.assign("/");
}

export function startViewAs(target: ViewAsTarget): void {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(target));
  } catch {
    return;
  }
  reloadHome();
}

export function clearViewAs(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* nothing stored */
  }
}

export function stopViewAs(): void {
  clearViewAs();
  reloadHome();
}
